import React, { FC } from "react";
import classNames from "classnames";
import { GatsbyImage } from "gatsby-plugin-image";
import { getGatsbyImage } from "gatsby-plugin-storyblok-image";
import {
  BlockHeader,
  HeaderLevel,
} from "@app/components/common/block-header/BlockHeader";
import { BlockBody } from "@app/components/common/block-body/BlockBody";
import { StoryblokUniversalLink } from "@app/components/common/storyblok-universal-link/StoryblokUniversalLink";
import { IPlan } from "@app/models/storyblok/blocks/plans";
import { BlockColorMap, BulletpointColorMap } from "./constants";
import { BlockColor } from "./enums";

type Props = {
  groupId: string;
  isActive: boolean;
  onSelect: (groupId: string) => void;
} & IPlan;

export const Plan: FC<Props> = ({
  condition,
  title,
  text,
  features,
  color,
  link,
  image,
  groupId,
  isActive,
  onSelect,
}) => (
  <div
    onClick={() => onSelect(groupId)}
    className={classNames(
      "relative w-full",
      "text-regular text-primary-content origin-bottom duration-700",
      "lg:text-extra-small lg:max-w-1/3",
      "xxl:text-regular",
      {
        "lg:scale-[95%]": !isActive,
      },
    )}
  >
    {image ? (
      <GatsbyImage
        image={getGatsbyImage(image.filename, {
          layout: "fullWidth",
        })}
        alt={"Plan"}
        className="aspect-[1.6] w-full rounded-t-lg"
      />
    ) : null}

    <div
      className={classNames(
        "bg-dark flex flex-col justify-between gap-5 rounded-b-lg px-6 py-8",
        "xl:rounded-b-3xl",
        "xxl:gap-10 xxl:px-10 xxl:pt-7.5 xxl:pb-11",
      )}
    >
      <div
        className={classNames(
          "text-base-400",
          "xl:text-regular",
          "xxl:text-medium",
        )}
      >
        <BlockHeader
          richText={title}
          headerLevel={HeaderLevel.H4}
          className={classNames(
            "text-medium text-primary-content mb-1.5",
            "xl:text-medium-large",
            "xxl:text-large",
          )}
        />
        <BlockBody richText={text} />
      </div>
      <div className={classNames("space-y-6", "lg:space-y-8")}>
        <div
          className={classNames(
            "[&_ul]:space-y-1",
            "[&_li]:relative [&_li]:pl-6",
            "[&_li]:before:absolute [&_li]:before:top-2 [&_li]:before:left-0 [&_li]:before:h-2.5 [&_li]:before:w-2.5",
            "[&_li]:before:rounded-full",
            "[&_li]:lg:before:top-1.5",
            "[&_li]:xxl:before:top-2",
            BulletpointColorMap[color ?? BlockColor.BLUE],
          )}
        >
          <BlockBody richText={features} />
        </div>
        <StoryblokUniversalLink data={link}>
          <button
            className={classNames(
              "text-base-content flex w-full items-center justify-center rounded-full p-3 text-base leading-[1.75rem] duration-300 hover:opacity-90",
              color === BlockColor.ORANGE
                ? "bg-primary-content"
                : BlockColorMap[color ?? BlockColor.BLUE],
            )}
            role="button"
          >
            <BlockBody richText={condition} />
          </button>
        </StoryblokUniversalLink>
      </div>
    </div>
    <div
      className={classNames(
        "absolute top-0 right-0 bottom-0 left-0 hidden cursor-pointer rounded-lg duration-700 lg:block",
        { "bg-base-content/50 visible": !isActive, invisible: isActive },
      )}
    />
  </div>
);
